<template>
  <ul
    class="
      flex min-h-full grow translate-x-0 flex-col leading-chill
      ease-[cubic-bezier(1,0.5,0.8,1)]
      lg:transition-[clip-path,opacity]
      max-lg:w-full max-lg:transition-transform max-lg:duration-250
    "
    :class="[
      isActiveItem || navigation.getHeight === 0
        ? `
          max-lg:translate-x-0 max-lg:ease-out
          lg:opacity-100 lg:duration-[300ms,0ms]
        `
        :`
          pointer-events-none
          max-lg:translate-x-full max-lg:ease-in
          lg:opacity-0 lg:delay-[0,100ms] lg:duration-[300ms,200ms]
        `,
      {
        'relative bg-gray-100 max-lg:overflow-hidden lg:h-full lg:w-1/3 xl:w-1/4': level === 0,
        'absolute top-0 z-10 w-full lg:left-full': level > 0,
        'bg-gray-250': level === 1,
        'bg-gray-255': level === 2,
        '[clip-path:inset(0%_0%_0%_0%)]': level === 0 && navigation.getActiveLevel === 0,
        '[clip-path:inset(0%_-100%_0%_0%)]': level === 0 && navigation.getActiveLevel === 1,
        '[clip-path:inset(0%_-200%_0%_0%)]': level === 0 && navigation.getActiveLevel === 2,
      },
    ]"
  >
    <li
      ref="navItemsRef"
      class="py-6"
    >
      <div
        v-if="level > 0"
        class="
          -mt-6 mb-4 bg-red-500 py-1 leading-relaxed
          lg:hidden
        "
      >
        <a
          href="#"
          class="
            group flex w-full px-5
            lg:pointer-events-none
          "
          :tabindex="isActiveItem && navigation.getOpen ? 0 : -1"
          @click.prevent="handleBackClick(level)"
        >
          <IconArrowLeft
            class="
              my-1.5 w-2 shrink-0 text-white transition-transform
              group-hover-focus:-translate-x-0.5
            "
          />
          <span class="sr-only">{{ $t('back') }}</span>
        </a>
      </div>
      <div
        v-for="item in items"
        :key="item.id"
        class="
          flex flex-wrap items-start overflow-hidden py-1 leading-relaxed
          max-lg:last:pb-6
          lg:whitespace-nowrap
        "
        :class="[
          // navigation.getActiveLevel0Item === item.page.id || navigation.getActiveLevel1Item === item.page.id
          //   ? `text-red-500`
          //   : 'text-black', //THIS IS HOW THE OLD NAV WAS, BUT IT SEEMS BETTER WIHOUT THIS
          {
            'bg-gray-250': navigation.getActiveLevel0Item === item.page.id,
            'bg-gray-255': navigation.getActiveLevel1Item === item.page.id,
          },
        ]"
      >
        <template v-if="item.page.url">
          <NuxtLink
            class="
              min-w-0 grow cursor-pointer whitespace-normal px-5
              hover-focus:text-red-500
              lg:px-10
            "
            :class="{ 'underline underline-offset-4': item.page.url === route.path }"
            :to="item.page.url"
            :tabindex="isActiveItem && navigation.getOpen ? 0 : -1"
            @click="handleLinkClick(item)"
          >
            {{ item.page.title }}
          </NuxtLink>
        </template>
        <template v-else>
          <a
            href="#"
            class="
              group flex min-w-0 grow cursor-pointer items-start justify-between
              whitespace-normal px-5
              hover-focus:text-red-500
              lg:px-10
            "
            :tabindex="isActiveItem && navigation.getOpen ? 0 : -1"
            @click.prevent="item.children && item.children.length
              ? handleChapterClick(item)
              : null"
          >
            <span>{{ item.page.title }}</span>
            <IconArrowRight
              v-if="item.children && item.children.length"
              class="
                ml-2 mt-1.5 w-2 shrink-0 transition-transform duration-150
                ease-in
                group-hover-focus:translate-x-1
              "
            />
          </a>
        </template>
        <NavItemsRecursive
          v-if="item.children && item.children.length"
          :item-id="item.page.id"
          :items="item.children"
          :level="item.depth"
        />
      </div>
    </li>
  </ul>
</template>

<script lang="ts" setup>
const props = defineProps({
  itemId: {
    type: String,
    default: '',
  },
  items: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
})

const route = useRoute()
const navigation = useNavigationStore()

const navItemsRef = ref<HTMLElement | null>(null)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleLinkClick = (item: any) => {
  navigation.toggle()
  handleChapterClick(item)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleChapterClick = (item: any) => {
  navigation.setActiveLevel(item.depth)
  if (item.depth === 1) {
    navigation.setActiveLevel0Item(item.page.id)
    navigation.setActiveLevel1Item(null)
  }

  if (item.depth === 2) {
    navigation.setActiveLevel1Item(item.page.id)
    navigation.setActiveLevel2Item(null)
  }

  if (item.depth === 3) {
    navigation.setActiveLevel2Item(item.page.id)
  }
}

const handleBackClick = (level: number) => {
  navigation.setActiveLevel(level)
  switch (level) {
    case 1:
      navigation.setActiveLevel0Item(null)
      navigation.setActiveLevel1Item(null)
      navigation.setActiveLevel2Item(null)
      break
    case 2:
      navigation.setActiveLevel1Item(null)
      navigation.setActiveLevel2Item(null)
      break
  }
}

// Function to find item by URL and return its page.id and its parent's page.id
const getInitialActiveIds = (
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: Record<string, any>, // Change to Record<string, any>
  parentIds: string[] = [],
): string[] | null => {
  const itemsArray = Object.values(items) // Convert object to array
  for (const item of itemsArray) {
    if (item.page.url === url) {
      return [...parentIds, item.page.id]
    }
    if (item.children) {
      const result = getInitialActiveIds(url, item.children, [
        ...parentIds,
        item.page.id,
      ])
      if (result) {
        return result
      }
    }
  }
  return null
}

const setInitialActiveItems = (ids: string[] | null) => {
  if (ids) {
    switch (ids.length) {
      case 1:
        navigation.setActiveLevel0Item(ids[0])
        navigation.setActiveLevel(1)
        break
      case 2:
        navigation.setActiveLevel0Item(ids[0])
        navigation.setActiveLevel1Item(ids[1])
        navigation.setActiveLevel(2)
        break
      case 3:
        navigation.setActiveLevel0Item(ids[0])
        navigation.setActiveLevel1Item(ids[1])
        navigation.setActiveLevel2Item(ids[2])
        navigation.setActiveLevel(3)
        break
    }
  }
}

const isActiveItem = computed(() => {
  return (
    props.level === 0
    || navigation.getActiveLevel0Item === props.itemId
    || navigation.getActiveLevel1Item === props.itemId
  )
})

const getHighestItem = () => {
  if (navItemsRef.value) {
    if (navigation.getHeight <= navItemsRef.value.scrollHeight) {
      navigation.setHeight(navItemsRef.value.scrollHeight)
    }
  }
}

onMounted(() => {
  const initialActiveIds = getInitialActiveIds(route.path, props.items)

  setInitialActiveItems(initialActiveIds)

  getHighestItem()
})

onUpdated(() => {
  getHighestItem()
})
</script>
